import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { newPaginationInput, PaginationInput } from "src/api/types";
import { useModalAction } from "src/components/modal-views/context";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CopyIcon } from "src/components/icons/copy-icon";
import moment from "moment";
import { Table } from "antd";
import { DateRangePicker } from "rsuite";
import { PlusIcon } from "src/components/icons/plus-icon";
import { DateRange } from "rsuite/esm/DateRangePicker";
import TerminalFormModal from "src/components/walletTerminal/TerminalFormModal";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

const WalletTerminal = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { openModal, closeModal } = useModalAction();

  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.WALLET_TERMINAL
  );
  const canViewWalletTerminal = canView || canList;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });

  const [terminalModalOpen, setTerminalModalOpen] = useState(false);
  const [editTerminalData, setEditTerminalData] = useState<any>(null);

  const [total, setTotal] = useState(0);
  const [isCopied, setIsCopied] = useState<{ [key: string]: boolean }>({});
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | null>(null);

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  const [startDate, endDate] = dateRangeValue || [];
  const { afterToday } = DateRangePicker;

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      page: page,
    });
  };

  const { mutate: getList, isLoading } = useMutation(
    client.walletTerminal.all,
    {
      onSuccess: (data: any) => {
        const dataObj = data.items;
        setTotal(data.totalcount);
        setDataSource(
          dataObj.map((item: any) => ({
            key: item.id,
            id: item.id,
            api_key: item.api_key,
            api_salt: item.api_salt,
            api_secret: item.api_secret,
            api_url: item.api_url,
            iframe_url: item.iframe_url,
            provider_name: item.provider_name,
            provider_identifier: item.provider_identifier,
            server_ip: item.server_ip,
            updated_at: item.updated_at,
            created_at: item.created_at,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: addTerminal, isLoading: addTerminalLoading } = useMutation(
    client.walletTerminal.create,
    {
      onSuccess: () => {
        setAlertText("Terminal Added Successfully");
        setTerminalModalOpen(false);
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") setAlertText(NetworkDisconnectMsg);
        else if (error.response)
          setAlertText(getErrorMessage(error.response.data));
        else setAlertText(APISomethingWrongMsg);

        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: deleteTerminal, isLoading: deleteTerminalLoading } =
    useMutation(client.walletTerminal.remove, {
      onSuccess: (data: any) => {
        setAlertText("Terminal Deleted");
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: updateTerminal, isLoading: updateTerminalLoading } =
    useMutation(client.walletTerminal.update, {
      onSuccess: (data: any) => {
        setAlertText("Terminal Updated");
        setTerminalModalOpen(false);
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const handleTerminalSubmit = (formData: any) => {
    if (editTerminalData) {
      if (!canEdit) {
        setAlertText("You do not have permission to edit terminals");
        setIsAlertOpened(true);
        return;
      }
      updateTerminal({
        id: editTerminalData.id,
        ...formData,
      });
    } else {
      if (!canAdd) {
        setAlertText("You do not have permission to add terminals");
        setIsAlertOpened(true);
        return;
      }
      addTerminal(formData);
    }
  };

  const onCallback = (bSuccess: boolean) => {
    bSuccess && getList(searchParams);
  };

  const onDelete = (key: number) => {
    if (!canDelete) {
      setAlertText("You do not have permission to delete terminals");
      setIsAlertOpened(true);
      return;
    }
    openModal("GENERAL_DELETE", {
      id: key,
      callback: () => {
        deleteTerminal({ id: key });
        setTimeout(() => {
          closeModal();
        }, 100);
      },
      isLoading: () => deleteTerminalLoading,
    });
  };

  useEffect(() => {
    if (!isMounted || !canViewWalletTerminal) {
      return;
    }
    getList({
      ...searchParams,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, canViewWalletTerminal, searchParams.page, startDate, endDate]);

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      render: (value: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Name
        </p>
      ),
      dataIndex: "provider_name",
      key: "provider_name",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Provider Id
        </p>
      ),
      dataIndex: "provider_identifier",
      key: "provider_identifier",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value ?? "__"}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          API Key
        </p>
      ),
      dataIndex: "api_key",
      key: "api_key",
      render: (value: any, record: any) => {
        const handleCopy = (id: number) => {
          const copyKey = `${id}-live`;
          setIsCopied((prev: any) => ({ ...prev, [copyKey]: true }));
          setTimeout(() => {
            setIsCopied((prev: any) => ({ ...prev, [copyKey]: false }));
          }, 4000);
        };

        return (
          <div className="flex items-center gap-2">
            <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
              {value}
            </p>
            <CopyToClipboard
              text={value || ""}
              onCopy={() => handleCopy(record.id)}
            >
              <button
                className={`${
                  isCopied[`${record.id}-live`]
                    ? "text-green-500"
                    : "text-black"
                } hover:underline`}
              >
                <CopyIcon />
              </button>
            </CopyToClipboard>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          API Secret
        </p>
      ),
      dataIndex: "api_secret",
      key: "api_secret",
      render: (value: any, record: any) => {
        const handleCopy = (id: number) => {
          const copyKey = `${id}-secret`;
          setIsCopied((prev: any) => ({ ...prev, [copyKey]: true }));
          setTimeout(() => {
            setIsCopied((prev: any) => ({ ...prev, [copyKey]: false }));
          }, 4000);
        };

        return (
          <div className="flex items-center gap-2">
            <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
              {value}
            </p>
            <CopyToClipboard
              text={value || ""}
              onCopy={() => handleCopy(record.id)}
            >
              <button
                className={`${
                  isCopied[`${record.id}-secret`]
                    ? "text-green-500"
                    : "text-black"
                } hover:underline`}
              >
                <CopyIcon />
              </button>
            </CopyToClipboard>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          API Url
        </p>
      ),
      dataIndex: "api_url",
      key: "api_url",
      render: (value: any, record: any) => {
        const handleCopy = () => {
          const copyKey = `${record.id}-apiurl`;

          setIsCopied((prev: any) => ({ ...prev, [copyKey]: true }));

          setTimeout(() => {
            setIsCopied((prev: any) => ({ ...prev, [copyKey]: false }));
          }, 3000);
        };

        return (
          <div className="flex items-center gap-2">
            <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
              {value}
            </p>

            <CopyToClipboard text={value || ""} onCopy={handleCopy}>
              <button
                className={`${
                  isCopied[`${record.id}-apiurl`]
                    ? "text-green-500"
                    : "text-black"
                } hover:underline`}
              >
                <CopyIcon />
              </button>
            </CopyToClipboard>
          </div>
        );
      },
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Iframe Url
        </p>
      ),
      dataIndex: "iframe_url",
      key: "iframe_url",
      render: (value: any, record: any) => {
        const handleCopy = () => {
          const copyKey = `${record.id}-iframe`;

          setIsCopied((prev: any) => ({ ...prev, [copyKey]: true }));

          setTimeout(() => {
            setIsCopied((prev: any) => ({ ...prev, [copyKey]: false }));
          }, 3000);
        };

        return (
          <div className="flex items-center gap-2">
            <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
              {value}
            </p>

            <CopyToClipboard text={value || ""} onCopy={handleCopy}>
              <button
                className={`${
                  isCopied[`${record.id}-iframe`]
                    ? "text-green-500"
                    : "text-black"
                } hover:underline`}
              >
                <CopyIcon />
              </button>
            </CopyToClipboard>
          </div>
        );
      },
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Server IP
        </p>
      ),
      dataIndex: "server_ip",
      key: "server_ip",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated At
        </p>
      ),
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-[100px]",
      render: (_: any, record: any) => {
        if (!canEdit && !canDelete) {
          return "—";
        }
        return (
          <div className="flex gap-3 items-center">
            {canEdit && (
              <EditOutlined
                className="text-primary cursor-pointer text-[18px]"
                onClick={() => {
                  setEditTerminalData(record);
                  setTerminalModalOpen(true);
                }}
              />
            )}
            {canDelete && (
              <DeleteOutlined
                className="text-red-500 cursor-pointer text-[18px]"
                onClick={() => onDelete(Number(record.id))}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full h-full flex-col p-4 sm:p-6">
      {/* Centered content container with padding and rounded card */}
      <div className="w-full mx-auto px-2 lg:px-4">
        <div className="bg-white rounded-md shadow-sm p-4 sm:p-6">
          {/* Header: title + controls row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-[22px] sm:text-[28px] font-manrope text-primary font-medium leading-tight">
                Wallet Terminals
              </h1>
            </div>

            {/* Controls: datepicker + add button aligned */}
            <div className="flex items-center gap-3">
              <div className="w-full sm:w-auto">
                <DateRangePicker
                  format="dd/MM/yyyy"
                  size="lg"
                  placement="bottomEnd"
                  style={{ width: isMobile ? "100%" : 320 }}
                  value={dateRangeValue}
                  onChange={setDateRangeValue}
                  defaultCalendarValue={[prevMonth, now]}
                  shouldDisableDate={afterToday()}
                  ranges={[]}
                />
              </div>

              <PermissionGate
                module={[permissionConstants.WALLET_TERMINAL]}
                action="can_add"
              >
                <div>
                  <button
                    className="flex items-center gap-2 px-3 py-2 bg-white border rounded-md hover:bg-[#F3F4F6]"
                    onClick={() => {
                      setEditTerminalData(null);
                      setTerminalModalOpen(true);
                    }}
                  >
                    <span className="font-manrope text-[14px] font-medium text-primary">
                      Add New Terminal
                    </span>
                    <PlusIcon className="w-[18px] h-[18px] text-primary" />
                  </button>
                </div>
              </PermissionGate>
            </div>
          </div>

          {/* Table wrapper with horizontal scroll if needed */}
          {!canViewWalletTerminal ? (
            <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-4 text-center shadow-sm">
              <p className="font-manrope text-[16px] font-semibold text-secondary">
                You do not have permission to view wallet terminals.
              </p>
            </div>
          ) : (
            <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
              <Table
                loading={isLoading}
                showSorterTooltip={false}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                  total,
                  current: searchParams.page,
                  onChange: onPageChange,
                  position: ["bottomCenter"],
                  defaultPageSize: LIST_PAGE_SIZE,
                  showSizeChanger: false,
                }}
                rowClassName="editable-row"
                // scroll={{ x: "max-content" }}
              />
            </div>
          )}
        </div>
      </div>

      <TerminalFormModal
        open={terminalModalOpen}
        onClose={() => setTerminalModalOpen(false)}
        onSubmit={handleTerminalSubmit}
        loading={addTerminalLoading || updateTerminalLoading}
        initialData={editTerminalData}
      />
    </div>
  );
};

export default WalletTerminal;
